import type { User } from '@/server/api/schemas/user.types';
import { Claims } from '@auth0/nextjs-auth0';
import { LDContext } from '@launchdarkly/vercel-server-sdk';
import { ProviderConfig } from 'launchdarkly-react-client-sdk';

export const ldDefaultContext = {
  kind: 'org',
  key: 'offer-tool-international',
};

export const launchDarklyConfig: ProviderConfig = {
  clientSideID: process.env.LD_CLIENT_SIDE_ID ?? '',
  context: ldDefaultContext,
};

/**
 * Builds a LaunchDarkly multi-context from user data
 * Multi-context includes user, tenant, and country contexts
 * @param user - User data from the /me endpoint
 * @returns LaunchDarkly multi-context or default context if user is null/undefined
 */
export const buildMultiContext = (user: User): LDContext => {
  const multiContext: LDContext = {
    kind: 'multi',
    user: {
      kind: 'user',
      key: user.id.toString(),
      email: user.email,
      name: user.name,
    },
  };

  // Add tenant context if available
  if (user.tenant) {
    multiContext.tenant = {
      kind: 'tenant',
      key: user.tenant.id,
      name: user.tenant.name,
    };
  }

  // Add country context - prefer tenant country, fallback to user country
  const country = user.tenant?.country ?? user.country;
  if (country?.countryCode) {
    multiContext.country = {
      kind: 'country',
      key: country.countryCode,
      name: country.name ?? undefined,
    };
  }

  return multiContext;
};

/**
 * Builds a basic LaunchDarkly multi-context from Auth0 session data
 * This is a fallback when full user data is not available
 * @param auth0User - Auth0 user claims from session
 * @returns LaunchDarkly multi-context with user information from Auth0
 */
export const buildMultiContextFromAuth0Session = (
  auth0User: Claims,
): LDContext => ({
  kind: 'multi',
  user: {
    kind: 'user',
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    key: auth0User.sub ?? auth0User.email ?? 'unknown',
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    email: auth0User.email,
    /* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */
    name: auth0User.nickname ?? auth0User.name,
  },
});

/**
 * Default multi-context fallback
 */
export const ldDefaultMultiContext: LDContext = {
  kind: 'multi',
  user: {
    kind: 'user',
    key: 'anonymous',
  },
};
